import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from '@/App'
import { env } from '@/lib/env'
import { applyTheme, initThemeListener } from '@/store'
import { registerServiceWorker } from '@/lib/pwa'
import '@/index.css'

/**
 * Escape hatch: /?sw-reset=1 tears down every service worker and cache, then
 * reloads clean. Recovering from a wedged worker otherwise means DevTools,
 * which is not something to ask a volunteer to do on a phone.
 */
if (new URLSearchParams(window.location.search).has('sw-reset')) {
  void (async () => {
    const registrations = await navigator.serviceWorker?.getRegistrations?.() ?? []
    await Promise.all(registrations.map((registration) => registration.unregister()))
    const keys = await caches.keys()
    await Promise.all(keys.map((key) => caches.delete(key)))
    window.location.replace(window.location.pathname)
  })()
}

/*
 * Theme, before the first paint.
 *
 * applyTheme() used to run only inside useSettingsStore.load(), which needs a
 * session — so every signed-out page rendered light regardless of the device,
 * and a dark-mode user went from the near-black boot screen to a white sign-in
 * screen. Running it here costs nothing and there is nothing to wait for: the
 * OS preference is available synchronously.
 *
 * initThemeListener() was exported and never called by anything, so a device
 * switching to dark on a schedule was ignored until the next reload.
 */
applyTheme()
initThemeListener()

const container = document.getElementById('root')
if (!container) throw new Error('Root element #root is missing from index.html')

createRoot(container).render(
  <StrictMode>
    {/*
      `basename` comes from VITE_BASE_PATH so the same bundle works at the root
      of services.mymdv.org and inside a sub-folder on shared hosting.
    */}
    <BrowserRouter basename={env.basePath}>
      <App />
    </BrowserRouter>
  </StrictMode>,
)

registerServiceWorker()
